/**
 * Search Analytics Service
 * Tracks and analyzes user search patterns for dashboard insights
 * Now includes PostHog data for comprehensive tracking across all sources
 */

import { apiService } from './api';

interface SearchEvent {
  id: string;
  query: string;
  timestamp: number;
  spaceId?: string;
  resultsCount: number;
  responseTime: number;
  successful: boolean;
  userId: string;
}

interface SearchAnalytics {
  totalSearches: number;
  searchesThisWeek: number;
  searchesToday: number;
  averageResponseTime: number;
  successRate: number;
  topQueries: Array<{ query: string; count: number; lastUsed: number }>;
  searchPatterns: {
    peakHours: Array<{ hour: number; count: number }>;
    topSpaces: Array<{ spaceId: string; spaceName: string; count: number }>;
    queryTypes: {
      semantic: number;
      keyword: number;
      exact: number;
    };
  };
  weeklyTrend: Array<{ date: string; count: number; avgResponseTime: number }>;
}

interface PostHogSearchData {
  searchesThisWeek: number;
  searchesToday: number;
  totalSearches: number;
  sources: {
    api: number;
    custom_gpt: number;
    n8n: number;
    zapier: number;
    socket: number;
    mcp: number;
  };
}

class SearchAnalyticsService {
  private static instance: SearchAnalyticsService;
  private searchEvents: SearchEvent[] = [];
  private readonly STORAGE_KEY = 'memara_search_analytics';
  private readonly MAX_EVENTS = 1000; // Keep last 1000 searches

  static getInstance(): SearchAnalyticsService {
    if (!SearchAnalyticsService.instance) {
      SearchAnalyticsService.instance = new SearchAnalyticsService();
    }
    return SearchAnalyticsService.instance;
  }

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Track a search event (frontend only)
   */
  trackSearch(
    query: string,
    resultsCount: number,
    responseTime: number,
    spaceId?: string,
    userId: string = 'anonymous'
  ): void {
    const event: SearchEvent = {
      id: `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      query: query.toLowerCase().trim(),
      timestamp: Date.now(),
      spaceId,
      resultsCount,
      responseTime,
      successful: resultsCount > 0,
      userId
    };

    this.searchEvents.push(event);

    // Keep only recent events
    if (this.searchEvents.length > this.MAX_EVENTS) {
      this.searchEvents = this.searchEvents.slice(-this.MAX_EVENTS);
    }

    this.saveToStorage();
    console.log('🔍 Search tracked:', { query, resultsCount, responseTime });
  }

  /**
   * Get comprehensive search analytics including PostHog data
   */
  async getAnalytics(userId?: string): Promise<SearchAnalytics> {
    const now = Date.now();
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
    const oneDayAgo = now - (24 * 60 * 60 * 1000);

    // Get frontend search data
    const userEvents = userId 
      ? this.searchEvents.filter(e => e.userId === userId)
      : this.searchEvents;

    const recentEvents = userEvents.filter(e => e.timestamp >= oneWeekAgo);
    const todayEvents = userEvents.filter(e => e.timestamp >= oneDayAgo);

    // Get PostHog search data for comprehensive tracking
    let posthogData: PostHogSearchData = {
      searchesThisWeek: 0,
      searchesToday: 0,
      totalSearches: 0,
      sources: {
        api: 0,
        custom_gpt: 0,
        n8n: 0,
        zapier: 0,
        socket: 0,
        mcp: 0
      }
    };

    try {
      // Fetch PostHog search analytics from backend
      const response = await apiService.getSearchAnalytics();
      if (response && response.data) {
        posthogData = response.data;
      }
    } catch (error) {
      console.warn('Failed to fetch PostHog search data:', error);
    }

    // Combine frontend and PostHog data
    const totalSearches = userEvents.length + posthogData.totalSearches;
    const searchesThisWeek = recentEvents.length + posthogData.searchesThisWeek;
    const searchesToday = todayEvents.length + posthogData.searchesToday;

    // Calculate success rate from available data
    const successfulSearches = userEvents.filter(e => e.successful).length;
    const successRate = totalSearches > 0 ? (successfulSearches / totalSearches) * 100 : 0;

    // Calculate average response time from frontend data
    const responseTimes = userEvents.map(e => e.responseTime).filter(t => t > 0);
    const averageResponseTime = responseTimes.length > 0 
      ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
      : 0;

    // Generate weekly trend combining both data sources
    const weeklyTrend = this.generateWeeklyTrend(recentEvents, posthogData);

    // Analyze query types
    const queryTypes = this.analyzeQueryTypes(userEvents);

    // Get top queries from frontend data
    const queryCounts = new Map<string, { count: number; lastUsed: number }>();
    userEvents.forEach(event => {
      const existing = queryCounts.get(event.query) || { count: 0, lastUsed: 0 };
      queryCounts.set(event.query, {
        count: existing.count + 1,
        lastUsed: Math.max(existing.lastUsed, event.timestamp)
      });
    });

    const topQueries = Array.from(queryCounts.entries())
      .map(([query, data]) => ({ query, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Analyze peak hours
    const peakHours = this.analyzePeakHours(userEvents);

    // Get top spaces
    const spaceCounts = new Map<string, { spaceId: string; spaceName: string; count: number }>();
    userEvents.forEach(event => {
      if (event.spaceId) {
        const existing = spaceCounts.get(event.spaceId) || { spaceId: event.spaceId, spaceName: 'Unknown', count: 0 };
        spaceCounts.set(event.spaceId, { ...existing, count: existing.count + 1 });
      }
    });

    const topSpaces = Array.from(spaceCounts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalSearches,
      searchesThisWeek,
      searchesToday,
      averageResponseTime,
      successRate,
      topQueries,
      searchPatterns: {
        peakHours,
        topSpaces,
        queryTypes
      },
      weeklyTrend
    };
  }

  /**
   * Get quick stats for dashboard (enhanced with PostHog data)
   */
  async getQuickStats(userId?: string): Promise<{ searchesThisWeek: number; searchesToday: number; successRate: number }> {
    const analytics = await this.getAnalytics(userId);
    return {
      searchesThisWeek: analytics.searchesThisWeek,
      searchesToday: analytics.searchesToday,
      successRate: analytics.successRate
    };
  }

  /**
   * Generate weekly trend combining frontend and PostHog data
   */
  private generateWeeklyTrend(frontendEvents: SearchEvent[], posthogData: PostHogSearchData) {
    const weeklyTrend = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Count frontend events for this day
      const dayStart = date.getTime();
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      const dayEvents = frontendEvents.filter(e => e.timestamp >= dayStart && e.timestamp < dayEnd);
      
      // Estimate PostHog events for this day (distribute weekly total)
      const estimatedPostHogEvents = Math.floor(posthogData.searchesThisWeek / 7);
      
      const totalCount = dayEvents.length + estimatedPostHogEvents;
      const avgResponseTime = dayEvents.length > 0 
        ? dayEvents.reduce((sum, e) => sum + e.responseTime, 0) / dayEvents.length 
        : 0;
      
      weeklyTrend.push({
        date: dateStr,
        count: totalCount,
        avgResponseTime
      });
    }
    
    return weeklyTrend;
  }

  /**
   * Analyze query types using simple heuristics
   */
  private analyzeQueryTypes(events: SearchEvent[]): { semantic: number; keyword: number; exact: number } {
    let semantic = 0;
    let keyword = 0;
    let exact = 0;

    events.forEach(event => {
      const query = event.query.toLowerCase();
      
      if (query.includes('"') || query.length < 3) {
        exact++;
      } else if (query.split(' ').length <= 2) {
        keyword++;
      } else {
        semantic++;
      }
    });

    return { semantic, keyword, exact };
  }

  /**
   * Analyze peak hours
   */
  private analyzePeakHours(events: SearchEvent[]): Array<{ hour: number; count: number }> {
    const hourCounts = new Array(24).fill(0);
    
    events.forEach(event => {
      const date = new Date(event.timestamp);
      const hour = date.getHours();
      hourCounts[hour]++;
    });
    
    return hourCounts.map((count, hour) => ({ hour, count }));
  }

  /**
   * Load events from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.searchEvents = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load search analytics from storage:', error);
      this.searchEvents = [];
    }
  }

  /**
   * Save events to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.searchEvents));
    } catch (error) {
      console.warn('Failed to save search analytics to storage:', error);
    }
  }

  /**
   * Clear all analytics data
   */
  clearAnalytics(): void {
    this.searchEvents = [];
    this.saveToStorage();
    console.log('🗑️ Search analytics cleared');
  }

  /**
   * Clear all demo data and reset analytics
   * @deprecated Demo data generation removed - use real search tracking
   */
  clearDemoData(): void {
    this.searchEvents = [];
    this.saveToStorage();
    console.log('🗑️ Demo data cleared - using real search analytics only');
  }

  /**
   * Force clear all stored analytics data (for debugging)
   */
  clearAllData(): void {
    this.searchEvents = [];
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('🗑️ All search analytics data cleared from storage');
  }

  /**
   * Debug method to inspect current analytics data
   */
  debugAnalytics(userId?: string): void {
    this.getAnalytics(userId).then(analytics => {
    console.log('🔍 Current Search Analytics Data:', {
      totalSearches: analytics.totalSearches,
      searchesThisWeek: analytics.searchesThisWeek,
      successRate: analytics.successRate,
      averageResponseTime: analytics.averageResponseTime,
      eventsCount: this.searchEvents.length,
      userId: userId
      });
    });
  }
}

export const searchAnalytics = SearchAnalyticsService.getInstance();

// Export types
export type { SearchAnalytics, SearchEvent }; 